import { createThunk, model, Model } from 'mst-collection';
import { cast, getRoot, types } from 'mobx-state-tree';
import { format } from 'date-fns';

import { TutorialUserStatsModel } from './TutorialUserStatsModel';
import {
  Api,
  ITutorialTimestampResponse,
  LikeStatusEnum,
  TutorialSaveStatusEnum,
} from '@api';
import { ThunkAbortController } from '../utils';
import { TutorialSubtitlesModel } from './TutorialSubtitlesModel';
import type { RootModelType } from '../RootModel';
import { TutorialTimestampModel } from './TutorialTimestampModel';
import { TariffPlanCodeEnum } from '@constants';
import { ProcessedCommentListModel } from '../Comments/CommentListModel';
import { formatPlayerTimeFromMs } from '@utils';
import { TutorialTagsListModel } from '@models/Tags/TutorialTagsListModel';
import { PhotoModel } from '@models/common/PhotoModel';

export class Tutorial extends Model({
  id: types.identifierNumber,
  title: types.string,
  cover: types.string,
  covers: types.array(PhotoModel),
  description: types.string,
  uploadDate: types.string,
  duration: types.number,
  isForTrial: false,
  views: 0,
  likes: 0,
  userStats: types.maybe(TutorialUserStatsModel),
  source: types.maybe(types.string),
  subtitles: types.optional(TutorialSubtitlesModel, {}),
  timestamps: types.array(TutorialTimestampModel),
  comments: types.optional(ProcessedCommentListModel, {}),
  tags: types.optional(TutorialTagsListModel, {}),
  courseId: types.maybeNull(types.number),
}) {
  incrementLikes() {
    this.likes = this.likes + 1;
  }

  decrementLikes() {
    this.likes = this.likes - 1;
  }

  updateSaved() {
    const savedList =
      getRoot<RootModelType>(this).tutorial.listsMap.special.saved;

    if (savedList.fetch.hasEverBeenRun) {
      const isSaved = this.userStats?.isSaved;

      if (isSaved) {
        savedList.removeById(this.id);
      } else {
        savedList.addToBegin(this.id);
      }
    }

    this.userStats?.toggleIsSaved();
  }

  updateHistory() {
    const historyList =
      getRoot<RootModelType>(this).tutorial.listsMap.special.history;

    if (!historyList.fetch.hasEverBeenRun) {
      return;
    }

    if (historyList.findById(this.id)) {
      // Remove from current position in order to add to the begin (move to the begin)
      historyList.removeById(this.id);
    }

    historyList.addToBegin(this.id);
  }

  get poster() {
    return this.covers[0];
  }

  get isAvailableForCurrentPlan(): boolean {
    return !(
      getRoot<RootModelType>(this).viewer.currentUser?.tariffPlan
        .id === TariffPlanCodeEnum.Trial && !this.isForTrial
    );
  }

  get formattedUploadDate() {
    return format(new Date(this.uploadDate), 'dd/MM/yyyy');
  }

  get formattedUploadDateTexted() {
    return format(new Date(this.uploadDate), 'MMM dd, yyyy');
  }

  get formattedDuration() {
    return formatPlayerTimeFromMs(this.duration);
  }

  get timestampsAsArray() {
    return this.timestamps.slice();
  }

  get streamSource() {
    return Api.Hub.getTutorialStreamSourceUrl({ id: this.id });
  }

  private _toggleSaveAbortController = new ThunkAbortController();
  private _toggleLikeAbortController = new ThunkAbortController();

  toggleSave = createThunk(() => {
    return async function toggleSave(this: Tutorial) {
      try {
        // Cancel previous request if it in progress
        this._toggleSaveAbortController.abort();

        const newStatus = this.userStats?.isSaved
          ? TutorialSaveStatusEnum.Unsave
          : TutorialSaveStatusEnum.Save;

        this.updateSaved();

        await Api.Hub.updateSaveStatus(
          {
            id: this.id,
            status: newStatus,
          },
          this._toggleSaveAbortController.create(),
        );

        this._toggleSaveAbortController.remove();
      } catch (error) {
        // If request was canceled do nothing
        if (ThunkAbortController.isCanceled(error)) {
          return;
        }
        // Change save status and item back
        this.updateSaved();

        throw error;
      }
    };
  });

  toggleLike = createThunk(() => {
    return async function toggleLike(this: Tutorial) {
      try {
        // Cancel previous request if it in progress
        this._toggleLikeAbortController.abort();

        const newStatus = this.userStats?.isLiked
          ? LikeStatusEnum.Unlike
          : LikeStatusEnum.Like;

        if (this.userStats?.isLiked) {
          this.decrementLikes();
        } else {
          this.incrementLikes();
        }

        this.userStats?.toggleIsLiked();

        await Api.Hub.updateLikeStatus(
          {
            id: this.id,
            status: newStatus,
          },
          this._toggleLikeAbortController.create(),
        );

        this._toggleLikeAbortController.remove();
      } catch (error) {
        // If request was canceled do nothing
        if (ThunkAbortController.isCanceled(error)) {
          return;
        }

        // Change likes count and status back
        if (this.userStats?.isLiked) {
          this.decrementLikes();
        } else {
          this.incrementLikes();
        }

        this.userStats?.toggleIsLiked();

        throw error;
      }
    };
  });

  saveView = createThunk(() => {
    return async function saveView(this: Tutorial) {
      this.updateHistory();
      await Api.Hub.saveView({ id: this.id });
    };
  });

  saveWatchTime = createThunk(
    ({ ms, id }: { ms: number; id: number }) => {
      return async function saveWatchTime(this: Tutorial) {
        await Api.Hub.saveWatchTime({
          totalMilliseconds: Math.round(ms),
          id,
        });
      };
    },
  );

  private _setTimestamps(timestamps: ITutorialTimestampResponse[]) {
    this.timestamps = cast(timestamps);
  }

  fetchTimestamps = createThunk(() => {
    return async function fetchTimestamps(this: Tutorial) {
      const res = await Api.Hub.getTimestamps({ id: this.id });

      this._setTimestamps(res.data);
    };
  });
}

export const TutorialModel = model(Tutorial);
// We set this name manually because after building the project into production
// mode code will be obfuscated and in the build js code class Post will get
// another name, class R for example. But we need this name to check parentEntity
// type (parentEntityName), for this case, we are using model names. The best way
// is to use parent.is(PostModel) but due to an import cycled dependency issue we
// cannot import PostModel or Post class into needed files, so we are using parent.name
// to detect the parent's model type (Post, Tutorial, or Track, etc)
// TODO: find a way to import Models in needed files
// (take a look at parentEntityName and parentEntity in CommentListModel and LikesUserList)
TutorialModel.name = 'Tutorial';
